import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Building2, Landmark, ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useDialogs } from "@/context/DialogsContext";
import FileUploadField from "@/components/forms/FileUploadField";
import DatePickerField from "@/components/forms/DatePickerField";
import FormProgress from "@/components/forms/FormProgress";

const TYPE_DEMANDE_OPTIONS = ["Transport d'un meuble", "Achat Leboncoin / Marketplace", "Transport électroménager", "Déménagement", "Déchargement de camion", "Débarras", "Longue distance", "Autre demande"];
const VOLUME_OPTIONS = ["1 à 3 objets", "Quelques meubles + cartons", "Studio / petit appartement", "Camion presque rempli", "Je ne sais pas encore"];
const AIDE_OPTIONS = ["Camion + chauffeur uniquement", "Camion + chauffeur + aide au chargement", "Camion + chauffeur + aide au chargement et déchargement", "Je suis seul(e), j'ai besoin d'aide", "À définir ensemble"];
const OUI_NON_NSP = ["oui", "non", "je ne sais pas"];
const OBJETS_LOURDS_OPTIONS = ["Machine à laver", "Réfrigérateur", "Canapé", "Lit / matelas", "Armoire", "Électroménager", "Meuble lourd", "Aucun objet lourd", "Autre"];
const TYPE_BESOIN_OPTIONS = ["Transport ponctuel", "Livraison volumineuse", "Manutention", "Débarras", "Tournée régulière", "Longue distance", "Autre"];
const FREQUENCE_OPTIONS = ["Ponctuel", "Hebdomadaire", "Mensuel", "Régulier", "À définir"];
const TYPE_ORGANISME_OPTIONS = ["Collectivité", "Association", "Bailleur", "École", "Établissement médical", "Entreprise publique", "Autre"];

const PROFILES = [
  { key: "particulier", icon: Home, title: "Particulier" },
  { key: "entreprise", icon: Building2, title: "Entreprise / professionnel" },
  { key: "institution", icon: Landmark, title: "Institution / collectivité" },
];

const INITIAL_DATA = {
  type_demande: "", ville_depart: "", ville_arrivee: "", date_souhaitee: null, horaire_souhaite: "",
  volume: "", aide: "", etage_depart: "", ascenseur_depart: "", etage_arrivee: "", ascenseur_arrivee: "",
  stationnement_facile: "", objets_lourds: [],
  nom_entreprise: "", secteur_activite: "", type_besoin: "", adresse_depart: "", adresse_arrivee: "",
  frequence: "", besoin_facture: "", creneau_souhaite: "",
  nom_organisme: "", type_organisme: "", nature_demande: "", contraintes_acces: "",
  name: "", phone: "", email: "", message: "", consent: false,
};

export default function QuoteFormDialog() {
  const { quoteOpen, setQuoteOpen, quoteProfile } = useDialogs();
  const [profile, setProfile] = useState(quoteProfile);
  const [step, setStep] = useState(quoteProfile ? 1 : 0);
  const [data, setData] = useState(INITIAL_DATA);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dossier, setDossier] = useState("");

  const totalSteps = profile === "particulier" ? 4 : 3;

  useEffect(() => {
    if (quoteOpen && quoteProfile) {
      setProfile(quoteProfile);
      setStep((s) => (s === 0 ? 1 : s));
    }
  }, [quoteOpen, quoteProfile]);

  const reset = () => {
    setProfile(null);
    setStep(0);
    setData(INITIAL_DATA);
    setFiles([]);
    setSubmitted(false);
  };

  const handleOpenChange = (open) => {
    setQuoteOpen(open);
    if (!open) setTimeout(reset, 250);
  };

  const set = (key) => (value) => setData((prev) => ({ ...prev, [key]: value }));

  const toggleObjetLourd = (item) => {
    setData((prev) => {
      const list = prev.objets_lourds.includes(item)
        ? prev.objets_lourds.filter((o) => o !== item)
        : [...prev.objets_lourds, item];
      return { ...prev, objets_lourds: list };
    });
  };

  const chooseProfile = (key) => {
    setProfile(key);
    setStep(1);
  };

  const goNext = () => setStep((s) => Math.min(totalSteps, s + 1));
  const goBack = () => {
    if (step === 1) {
      if (!quoteProfile) {
        setProfile(null);
        setStep(0);
      }
    } else {
      setStep((s) => Math.max(1, s - 1));
    }
  };

  const buildPayload = () => {
    let lead_type = "devis_particulier";
    let details = {};
    if (profile === "particulier") {
      lead_type = "devis_particulier";
      details = {
        type_demande: data.type_demande, ville_depart: data.ville_depart, ville_arrivee: data.ville_arrivee,
        date_souhaitee: data.date_souhaitee ? data.date_souhaitee.toISOString().slice(0, 10) : "",
        horaire_souhaite: data.horaire_souhaite, volume: data.volume, aide: data.aide,
        etage_depart: data.etage_depart, ascenseur_depart: data.ascenseur_depart,
        etage_arrivee: data.etage_arrivee, ascenseur_arrivee: data.ascenseur_arrivee,
        stationnement_facile: data.stationnement_facile, objets_lourds: data.objets_lourds,
      };
    } else if (profile === "entreprise") {
      lead_type = "devis_entreprise";
      details = {
        nom_entreprise: data.nom_entreprise, secteur_activite: data.secteur_activite, type_besoin: data.type_besoin,
        adresse_depart: data.adresse_depart, adresse_arrivee: data.adresse_arrivee, frequence: data.frequence,
        besoin_facture: data.besoin_facture, creneau_souhaite: data.creneau_souhaite,
      };
    } else if (profile === "institution") {
      lead_type = "devis_institution";
      details = {
        nom_organisme: data.nom_organisme, type_organisme: data.type_organisme, nature_demande: data.nature_demande,
        adresse_depart: data.adresse_depart, adresse_arrivee: data.adresse_arrivee,
        contraintes_acces: data.contraintes_acces, besoin_devis_formel: true, besoin_facture: true,
      };
    }
    return {
      lead_type,
      name: data.name,
      phone: data.phone,
      email: data.email || null,
      message: data.message || null,
      details: {
        ...details,
        page: window.location.pathname,
        source_bouton: `devis-${profile}`,
        source_form: "quote_dialog",
      },
      files: files.map((f) => f.path),
      consent: data.consent,
    };
  };

  const handleSubmit = async () => {
    if (!data.name || !data.phone || !data.consent) {
      toast.error("Merci de renseigner votre nom, téléphone et d'accepter le consentement.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post("/leads", buildPayload());
      setDossier(res.data?.dossier_number || "");
      setSubmitted(true);
      toast.success("Votre demande a bien été envoyée !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = profile === "entreprise" ? "Demander un devis professionnel" : profile === "institution" ? "Demander un devis institutionnel" : "Recevoir mon devis";

  return (
    <Dialog open={quoteOpen} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="quote-form-dialog" className="max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl">
        <DialogTitle className="sr-only">Demande de devis</DialogTitle>
        {submitted ? (
          <div className="text-center py-10" data-testid="quote-form-success">
            <CheckCircle2 className="w-14 h-14 text-orange mx-auto mb-4" />
            <h3 className="font-heading font-bold text-2xl text-navy mb-2">Demande envoyée</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-6">
              Votre demande a bien été transmise à Express Delivery.
              {dossier && (
                <> Votre numéro de dossier est <strong className="text-navy">{dossier}</strong>.</>
              )}{" "}
              Nous allons étudier le trajet, le volume, les accès et les moyens nécessaires avant de revenir vers vous.
            </p>
            <Button data-testid="quote-form-close-success" onClick={() => handleOpenChange(false)} className="bg-navy text-white rounded-full px-8">
              Fermer
            </Button>
          </div>
        ) : step === 0 ? (
          <div>
            <h3 className="font-heading font-bold text-2xl text-navy mb-2">Obtenir un tarif clair en quelques clics</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Répondez simplement aux questions essentielles. Les photos ou vidéos sont optionnelles.
            </p>
            <p className="text-sm font-semibold text-navy mb-3">Vous êtes :</p>
            <div className="grid gap-3">
              {PROFILES.map((p) => (
                <button
                  key={p.key}
                  data-testid={`quote-select-profile-${p.key}`}
                  onClick={() => chooseProfile(p.key)}
                  className="flex items-center gap-3 border-2 border-gray-100 hover:border-orange rounded-xl px-4 py-3.5 text-left transition-colors"
                >
                  <p.icon className="w-5 h-5 text-navy" />
                  <span className="font-semibold text-navy text-sm">{p.title}</span>
                  <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <FormProgress step={step} total={totalSteps} />
            <p className="text-xs text-muted-foreground font-semibold mt-2 mb-4">Étape {step} sur {totalSteps}</p>

            <AnimatePresence mode="wait">
              <motion.div key={`${profile}-${step}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                {profile === "particulier" && renderParticulierStep(step, data, set, toggleObjetLourd)}
                {profile === "entreprise" && renderEntrepriseStep(step, data, set)}
                {profile === "institution" && renderInstitutionStep(step, data, set)}
                {step === totalSteps && (
                  <div className="mt-6 space-y-4">
                    <p className="text-sm font-semibold text-navy">Vos photos ou vidéos (optionnel)</p>
                    <FileUploadField files={files} onChange={setFiles} testId="quote-form-file-upload" />
                    <div className="grid sm:grid-cols-2 gap-4 mt-2">
                      <Input data-testid="quote-form-input-name" placeholder="Nom *" value={data.name} onChange={(e) => set("name")(e.target.value)} className="rounded-xl h-11" />
                      <Input data-testid="quote-form-input-phone" placeholder="Téléphone *" value={data.phone} onChange={(e) => set("phone")(e.target.value)} className="rounded-xl h-11" />
                    </div>
                    <Input data-testid="quote-form-input-email" placeholder="Email (optionnel)" value={data.email} onChange={(e) => set("email")(e.target.value)} className="rounded-xl h-11" />
                    <Textarea data-testid="quote-form-input-message" placeholder="Message complémentaire" value={data.message} onChange={(e) => set("message")(e.target.value)} className="rounded-xl" />
                    <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                      <Checkbox data-testid="quote-form-consent-checkbox" checked={data.consent} onCheckedChange={(v) => set("consent")(!!v)} className="mt-0.5" />
                      J'accepte que mes informations soient utilisées pour être recontacté au sujet de ma demande.
                    </label>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-between mt-8">
              <Button data-testid="quote-form-back-button" variant="ghost" onClick={goBack} className="text-navy font-semibold">
                <ArrowLeft className="w-4 h-4 mr-1" /> Retour
              </Button>
              {step < totalSteps ? (
                <Button data-testid="quote-form-next-button" onClick={goNext} className="bg-navy text-white rounded-full px-7 font-semibold">
                  Continuer <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button data-testid="quote-form-submit-button" onClick={handleSubmit} disabled={submitting} className="bg-orange hover:bg-orange-hover text-white rounded-full px-7 font-semibold">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} {submitLabel}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FieldLabel({ children }) {
  return <p className="text-sm font-semibold text-navy mb-1.5">{children}</p>;
}

function SelectField({ testId, placeholder, value, onChange, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="rounded-xl h-11">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function renderParticulierStep(step, data, set, toggleObjetLourd) {
  if (step === 1) {
    return (
      <div className="space-y-4">
        <div>
          <FieldLabel>Type de demande</FieldLabel>
          <SelectField testId="quote-particulier-type-demande" placeholder="Choisir un type de demande" value={data.type_demande} onChange={set("type_demande")} options={TYPE_DEMANDE_OPTIONS} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Ville / adresse de départ</FieldLabel>
            <Input data-testid="quote-particulier-ville-depart" value={data.ville_depart} onChange={(e) => set("ville_depart")(e.target.value)} className="rounded-xl h-11" />
          </div>
          <div>
            <FieldLabel>Ville / adresse d'arrivée</FieldLabel>
            <Input data-testid="quote-particulier-ville-arrivee" value={data.ville_arrivee} onChange={(e) => set("ville_arrivee")(e.target.value)} className="rounded-xl h-11" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Date souhaitée</FieldLabel>
            <DatePickerField testId="quote-particulier-date" value={data.date_souhaitee} onChange={set("date_souhaitee")} />
          </div>
          <div>
            <FieldLabel>Horaire souhaité</FieldLabel>
            <Input data-testid="quote-particulier-horaire" placeholder="ex : matin, 14h, flexible" value={data.horaire_souhaite} onChange={(e) => set("horaire_souhaite")(e.target.value)} className="rounded-xl h-11" />
          </div>
        </div>
      </div>
    );
  }
  if (step === 2) {
    return (
      <div className="space-y-4">
        <div>
          <FieldLabel>Volume approximatif</FieldLabel>
          <SelectField testId="quote-particulier-volume" placeholder="Choisir un volume" value={data.volume} onChange={set("volume")} options={VOLUME_OPTIONS} />
        </div>
        <div>
          <FieldLabel>Besoin d'aide</FieldLabel>
          <SelectField testId="quote-particulier-aide" placeholder="Choisir un besoin d'aide" value={data.aide} onChange={set("aide")} options={AIDE_OPTIONS} />
        </div>
      </div>
    );
  }
  if (step === 3) {
    return (
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Étage au départ</FieldLabel>
            <Input data-testid="quote-particulier-etage-depart" value={data.etage_depart} onChange={(e) => set("etage_depart")(e.target.value)} className="rounded-xl h-11" />
          </div>
          <div>
            <FieldLabel>Ascenseur au départ</FieldLabel>
            <SelectField testId="quote-particulier-ascenseur-depart" placeholder="oui / non / je ne sais pas" value={data.ascenseur_depart} onChange={set("ascenseur_depart")} options={OUI_NON_NSP} />
          </div>
          <div>
            <FieldLabel>Étage à l'arrivée</FieldLabel>
            <Input data-testid="quote-particulier-etage-arrivee" value={data.etage_arrivee} onChange={(e) => set("etage_arrivee")(e.target.value)} className="rounded-xl h-11" />
          </div>
          <div>
            <FieldLabel>Ascenseur à l'arrivée</FieldLabel>
            <SelectField testId="quote-particulier-ascenseur-arrivee" placeholder="oui / non / je ne sais pas" value={data.ascenseur_arrivee} onChange={set("ascenseur_arrivee")} options={OUI_NON_NSP} />
          </div>
        </div>
        <div>
          <FieldLabel>Stationnement facile</FieldLabel>
          <SelectField testId="quote-particulier-stationnement" placeholder="oui / non / je ne sais pas" value={data.stationnement_facile} onChange={set("stationnement_facile")} options={OUI_NON_NSP} />
        </div>
        <div>
          <FieldLabel>Objets lourds</FieldLabel>
          <div className="grid sm:grid-cols-2 gap-2">
            {OBJETS_LOURDS_OPTIONS.map((item) => (
              <label key={item} className="flex items-center gap-2 text-sm text-navy/80 border border-gray-100 rounded-lg px-3 py-2 cursor-pointer">
                <Checkbox data-testid={`quote-particulier-objet-${item}`} checked={data.objets_lourds.includes(item)} onCheckedChange={() => toggleObjetLourd(item)} />
                {item}
              </label>
            ))}
          </div>
        </div>
      </div>
    );
  }
  return null;
}

function renderEntrepriseStep(step, data, set) {
  if (step === 1) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground -mt-1 mb-1">
          Express Delivery accompagne les professionnels pour leurs besoins de transport, manutention, livraison volumineuse, débarras, interventions planifiées et tournées régulières en Normandie.
        </p>
        <div>
          <FieldLabel>Nom de l'entreprise</FieldLabel>
          <Input data-testid="quote-entreprise-nom" value={data.nom_entreprise} onChange={(e) => set("nom_entreprise")(e.target.value)} className="rounded-xl h-11" />
        </div>
        <div>
          <FieldLabel>Secteur d'activité</FieldLabel>
          <Input data-testid="quote-entreprise-secteur" value={data.secteur_activite} onChange={(e) => set("secteur_activite")(e.target.value)} className="rounded-xl h-11" />
        </div>
        <div>
          <FieldLabel>Type de besoin</FieldLabel>
          <SelectField testId="quote-entreprise-type-besoin" placeholder="Choisir un type de besoin" value={data.type_besoin} onChange={set("type_besoin")} options={TYPE_BESOIN_OPTIONS} />
        </div>
      </div>
    );
  }
  if (step === 2) {
    return (
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Adresse de départ</FieldLabel>
            <Input data-testid="quote-entreprise-adresse-depart" value={data.adresse_depart} onChange={(e) => set("adresse_depart")(e.target.value)} className="rounded-xl h-11" />
          </div>
          <div>
            <FieldLabel>Adresse d'arrivée</FieldLabel>
            <Input data-testid="quote-entreprise-adresse-arrivee" value={data.adresse_arrivee} onChange={(e) => set("adresse_arrivee")(e.target.value)} className="rounded-xl h-11" />
          </div>
        </div>
        <div>
          <FieldLabel>Fréquence</FieldLabel>
          <SelectField testId="quote-entreprise-frequence" placeholder="Choisir une fréquence" value={data.frequence} onChange={set("frequence")} options={FREQUENCE_OPTIONS} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Besoin de facture</FieldLabel>
            <SelectField testId="quote-entreprise-facture" placeholder="oui / non" value={data.besoin_facture} onChange={set("besoin_facture")} options={["oui", "non"]} />
          </div>
          <div>
            <FieldLabel>Créneau souhaité</FieldLabel>
            <Input data-testid="quote-entreprise-creneau" value={data.creneau_souhaite} onChange={(e) => set("creneau_souhaite")(e.target.value)} className="rounded-xl h-11" />
          </div>
        </div>
      </div>
    );
  }
  return null;
}

function renderInstitutionStep(step, data, set) {
  if (step === 1) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground -mt-1 mb-1">
          Express Delivery peut établir des devis formels et organiser des interventions structurées pour collectivités, associations, bailleurs, écoles, établissements médicaux, organismes publics ou privés.
        </p>
        <div>
          <FieldLabel>Nom de l'organisme</FieldLabel>
          <Input data-testid="quote-institution-nom" value={data.nom_organisme} onChange={(e) => set("nom_organisme")(e.target.value)} className="rounded-xl h-11" />
        </div>
        <div>
          <FieldLabel>Type d'organisme</FieldLabel>
          <SelectField testId="quote-institution-type" placeholder="Choisir un type d'organisme" value={data.type_organisme} onChange={set("type_organisme")} options={TYPE_ORGANISME_OPTIONS} />
        </div>
        <div>
          <FieldLabel>Nature de la demande</FieldLabel>
          <Textarea data-testid="quote-institution-nature" value={data.nature_demande} onChange={(e) => set("nature_demande")(e.target.value)} className="rounded-xl" />
        </div>
      </div>
    );
  }
  if (step === 2) {
    return (
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Adresse de départ</FieldLabel>
            <Input data-testid="quote-institution-adresse-depart" value={data.adresse_depart} onChange={(e) => set("adresse_depart")(e.target.value)} className="rounded-xl h-11" />
          </div>
          <div>
            <FieldLabel>Adresse d'arrivée</FieldLabel>
            <Input data-testid="quote-institution-adresse-arrivee" value={data.adresse_arrivee} onChange={(e) => set("adresse_arrivee")(e.target.value)} className="rounded-xl h-11" />
          </div>
        </div>
        <div>
          <FieldLabel>Contraintes d'accès</FieldLabel>
          <Textarea data-testid="quote-institution-contraintes" value={data.contraintes_acces} onChange={(e) => set("contraintes_acces")(e.target.value)} className="rounded-xl" />
        </div>
        <p className="text-xs text-muted-foreground">Un devis formel et une facture seront systématiquement fournis pour ce type de demande.</p>
      </div>
    );
  }
  return null;
}
